import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { apiClient } from '../../lib/api-client.js';
import { useGeolocationContext } from '../../contexts/GeolocationContext.js';
import type { AiSpot, AiExploreTheme, AiExploreResponse } from './types.js';
import { THEME_LABELS } from './types.js';
import { AiSpotCard } from './AiSpotCard.js';

function SpotSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col gap-2 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-100 rounded w-1/2" />
      <div className="h-3 bg-gray-100 rounded w-full" />
      <div className="h-3 bg-gray-100 rounded w-5/6" />
    </div>
  );
}

export function AiSpotListView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { latitude: userLat, longitude: userLng } = useGeolocationContext();
  const theme = (searchParams.get('theme') ?? 'any') as AiExploreTheme;
  const lat = parseFloat(searchParams.get('lat') ?? '');
  const lng = parseFloat(searchParams.get('lng') ?? '');
  const radiusKm = parseFloat(searchParams.get('radiusKm') ?? '3');

  const [spots, setSpots] = useState<AiSpot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSpots = useCallback(async () => {
    if (isNaN(lat) || isNaN(lng)) {
      setError('位置情報が取得できませんでした。');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await apiClient.post<AiExploreResponse>('/ai/explore', {
        lat,
        lng,
        radiusKm: isNaN(radiusKm) ? 3 : radiusKm,
        theme,
      });
      setSpots(res.spots);
    } catch {
      setError('AI探索に失敗しました。しばらく経ってから再試行してください。');
    } finally {
      setLoading(false);
    }
  }, [lat, lng, radiusKm, theme]);

  useEffect(() => {
    void fetchSpots();
  }, [fetchSpots]);

  const themeInfo = THEME_LABELS[theme] ?? THEME_LABELS['any'];

  return (
    <div
      className="fixed inset-0 bg-gray-50 flex flex-col"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* ヘッダー */}
      <div className="bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 active:bg-gray-200"
          aria-label="戻る"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 18l-6-6 6-6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xl leading-none">{themeInfo.icon}</span>
          <div>
            <h1 className="text-sm font-semibold text-gray-900">{themeInfo.label}</h1>
            <p className="text-xs text-gray-400">AI探索結果</p>
          </div>
        </div>
      </div>

      {/* コンテンツ */}
      <div
        className="flex-1 overflow-y-auto px-4 py-4"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)' }}
      >
        {loading && (
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <SpotSkeleton key={i} />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
            <span className="text-4xl">⚠️</span>
            <p className="text-sm text-gray-600">{error}</p>
            <button
              type="button"
              onClick={() => void fetchSpots()}
              className="bg-primary text-white rounded-full px-5 py-2 text-sm font-medium active:opacity-80"
            >
              再試行
            </button>
          </div>
        )}

        {!loading && !error && spots.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <span className="text-4xl">🗺️</span>
            <p className="text-sm text-gray-500">
              このエリアではスポットが
              <br />
              見つかりませんでした
            </p>
          </div>
        )}

        {!loading && !error && spots.length > 0 && (
          <div className="flex flex-col gap-3">
            <div className="mb-1">
              <p className="text-xs text-gray-400">{spots.length}件のスポットが見つかりました</p>
              <p className="text-xs text-amber-500 mt-0.5">
                AIが生成した情報です。実際の情報と異なる場合があります。
              </p>
            </div>
            {spots.map((spot, i) => (
              <AiSpotCard
                key={i}
                spot={spot}
                userLat={userLat ?? undefined}
                userLng={userLng ?? undefined}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
